import React, { useEffect, useState } from "react";
import {
    Breadcrumb,
    Divider,
    Layout,
    Row,
    Table,
    Typography,
    Modal,
    Button,
    Space,
    Input, Tag
} from "antd";

import Api from "../api";
import dayjs from "dayjs";

const { Title } = Typography;
const { Content } = Layout;

const FoodScans = () => {
    const [loading, setLoading] = useState(false);
    const [scans, setScans] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);

    const [selectedScan, setSelectedScan] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        loadScans();
    }, [page]);

    const loadScans = () => {
        setLoading(true);

        Api.get(
            `/admin/v2/food-scans?page=${page}&per_page=25`
        )
            .then(res => {
                const pagination = res.data.data;

                setScans(pagination.data);
                setMeta(pagination);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const openScan = (id) => {
        Api.get(`/admin/v2/food-scans/${id}`)
            .then(res => {
                setSelectedScan(res.data.data);
                setModalVisible(true);
            });
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            width: 80
        },
        {
            title: "Image",
            render: (_, row) => (
                <img
                    src={row.image_url}
                    alt=""
                    style={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: 8
                    }}
                />
            )
        },
        {
            title: "User",
            render: (_, row) => (
                <>
                    <div>{row.user?.name_f}</div>
                    <small>{row.user?.email}</small>
                </>
            )
        },
        {
            title: "Recognized Items",
            render: (_, row) => {
                if (!Array.isArray(row.response_data)) {
                    return "-";
                }

                return row.response_data
                    .map(item => item.matches?.[0]?.name)
                    .filter(Boolean)
                    .join(", ");
            }
        },
        {
            title: "Created",
            dataIndex: "created_at",
            render: (value) =>
                value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "-"
        },
        {
            title: "Actions",
            render: (_, row) => (
                <Button
                    type="primary"
                    onClick={() => openScan(row.id)}
                >
                    View
                </Button>
            )
        }
    ];

    const scaleNutrition = (nutrition, amount) => {
        const factor = (amount || 0) / 100;

        return {
            protein: (nutrition?.protein || 0) * factor,
            carbohydrate: (nutrition?.carbohydrate || 0) * factor,
            fat: (nutrition?.fat || 0) * factor,
            energy: (nutrition?.energy || 0) * factor,
        };
    };

    const nutritionTotals = (selectedScan?.response_data || []).reduce(
        (acc, item) => {
            const match = item.matches?.[0];
            const n = match?.nutrition;
            const amount = match?.estimatedAmount || 0;

            const scaled = scaleNutrition(n, amount);

            acc.protein += scaled.protein;
            acc.carbs += scaled.carbohydrate;
            acc.fat += scaled.fat;
            acc.energy += scaled.energy;

            return acc;
        },
        { protein: 0, carbs: 0, fat: 0, energy: 0 }
    );

    const getConfidenceColor = (confidence) => {
        if (confidence > 0.8) return "green";
        if (confidence > 0.6) return "orange";
        return "red";
    };


    return (
        <Content style={{ margin: "0 16px" }}>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Food Recognition</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Divider orientation="left">
                    <Title level={4} style={{ margin: 0 }}>
                        Food Scans ({meta.total || 0})
                    </Title>
                </Divider>

                <br />

                <Table
                    size="small"
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={scans}
                    pagination={{
                        current: page,
                        total: meta.total,
                        pageSize: meta.per_page,
                        showSizeChanger: false,
                    }}
                    onChange={(pagination) => {
                        setPage(pagination.current);
                    }}
                />

                <Modal
                    width={1200}
                    open={modalVisible}
                    footer={null}
                    onCancel={() => setModalVisible(false)}
                    title={`Food Scan #${selectedScan?.id}`}
                >
                    {selectedScan && (
                        <div>

                            <img
                                src={selectedScan.image_url}
                                alt=""
                                style={{
                                    width: "100%",
                                    maxHeight: 500,
                                    objectFit: "contain",
                                    marginBottom: 20
                                }}
                            />
                            <h3>Recognition Result</h3>

                            <Table
                                rowKey={(row, index) => index}
                                pagination={false}
                                dataSource={selectedScan.response_data?.map(item => {
                                    const match = item.matches?.[0];
                                    const amount = match?.estimatedAmount || 0;

                                    return {
                                        ...match,
                                        scaledNutrition: scaleNutrition(match?.nutrition, amount)
                                    };
                                }) || []}
                                columns={[
                                    {
                                        title: "Name",
                                        dataIndex: "name"
                                    },
                                    {
                                        title: "Confidence",
                                        dataIndex: "confidence",
                                        render: (_, row) => <Tag
                                            color={getConfidenceColor(row.confidence)}
                                            className="confidence-tag"
                                        >
                                            {Math.round(row.confidence * 100)}%
                                        </Tag>
                                    },
                                    {
                                        title: "Category",
                                        dataIndex: "category"
                                    },
                                    {
                                        title: "Amount (g)",
                                        dataIndex: "estimatedAmount"
                                    },
                                    {
                                        title: "Protein",
                                        render: (_, row) => row.scaledNutrition?.protein?.toFixed(1)
                                    },
                                    {
                                        title: "Carbs",
                                        render: (_, row) => row.scaledNutrition?.carbohydrate?.toFixed(1)
                                    },
                                    {
                                        title: "Fat",
                                        render: (_, row) => row.scaledNutrition?.fat?.toFixed(1)
                                    },
                                    {
                                        title: "Energy",
                                        render: (_, row) => row.scaledNutrition?.energy?.toFixed(0)
                                    }
                                ]}
                                summary={() => (
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                            <b>Total</b>
                                        </Table.Summary.Cell>

                                        <Table.Summary.Cell index={1} />
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} />

                                        <Table.Summary.Cell index={4}>
                                            <b>{nutritionTotals.protein.toFixed(1)}</b>
                                        </Table.Summary.Cell>

                                        <Table.Summary.Cell index={5}>
                                            <b>{nutritionTotals.carbs.toFixed(1)}</b>
                                        </Table.Summary.Cell>

                                        <Table.Summary.Cell index={6}>
                                            <b>{nutritionTotals.fat.toFixed(1)}</b>
                                        </Table.Summary.Cell>

                                        <Table.Summary.Cell index={7}>
                                            <b>{nutritionTotals.energy.toFixed(0)}</b>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                )}
                            />

                            <br />

                            <h3>Raw JSON</h3>

                            <pre
                                style={{
                                    border: "solid 1px #eee",
                                    padding: 20,
                                    maxHeight: 400,
                                    overflow: "auto"
                                }}
                            >
                                {JSON.stringify(
                                    selectedScan.response_data,
                                    null,
                                    2
                                )}
                            </pre>
                        </div>
                    )}
                </Modal>
            </div>
        </Content>
    );
};

export default FoodScans;